import type { Observable as ObservableType } from 'rxjs/Observable';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/mergeMap';
import type { DocNode } from '@atlaskit/adf-schema';
import type { Issue } from '@atlassian/jira-software-backlog-uif-types/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { transformCard } from '../../../model/issue/utils/index.tsx';
import type { Card } from '../../../model/types.tsx';
import { fetchIssue } from '../../../rest/fetch-issue/index.tsx';
import { inlineCreateIssue } from '../../../rest/inline-create-issue/index.tsx';
import type { InlineCreateResponse } from '../../../rest/inline-create-issue/types.tsx';
import type { EpicIssueTypeState } from '../../../state/reducers/config/epic/types.tsx';
import type { ServiceContext } from '../../service-context/index.tsx';
import { bulkCreateIssuesWithDescription } from '../../../rest/bulk-create-issue-with-description/index.tsx';
import type { BulkIssueCreationGraphqlResponse } from '../../../rest/bulk-create-issue-with-description/types.tsx';
import type { CreateCardServiceOptions } from './types.tsx';

const fetchNewlyCreatedIssue =
	(boardId: string) =>
	(inlineCreateResponse: InlineCreateResponse): ObservableType<Issue> =>
		fetchIssue(boardId, Number(inlineCreateResponse.issue.issueId));

const updateIssueConfigStore =
	(
		epicIssueType: EpicIssueTypeState | null,
		storeFieldsFromFetchIssuesData: (
			issues: Issue[] | null,
			epicIssueType: EpicIssueTypeState | null,
		) => void,
	) =>
	(issue: Issue): Issue => {
		storeFieldsFromFetchIssuesData([issue], epicIssueType);
		return issue;
	};

const transformIssueToCard = (issue: Issue): Card[] => [transformCard(issue)];

export function inlineCardCreateService(
	serviceContext: ServiceContext,
	{
		summary,
		issueTypeId,
		contexts,
		overrides,
		epicIssueType,
		storeFieldsFromFetchIssuesData,
	}: CreateCardServiceOptions,
): ObservableType<Card[]> {
	return inlineCreateIssue({
		summary,
		issueTypeId,
		contexts,
		overrides,
	})
		.flatMap(fetchNewlyCreatedIssue(serviceContext.boardId))
		.map(updateIssueConfigStore(epicIssueType, storeFieldsFromFetchIssuesData))
		.map(transformIssueToCard);
}

type BulkCreateServiceProps = {
	epicIssueType: EpicIssueTypeState | null;
	projectId: number;
	cloudId: string;
	fixVersions: string[];
	assigneeId: string | null;
	parentId: string | null | undefined;
	labels: string[];
	issueTypeId: string;
	sprintFieldId?: string;
	sprintIds?: string[];
	cards: Array<{
		summary: string;
		description: string | DocNode;
	}>;
	// eslint-disable-next-line jira/react/handler-naming
	storeFieldsFromFetchIssuesData: (
		issues: Issue[] | null,
		epicIssueType: EpicIssueTypeState | null,
	) => void;
};

export function inlineCardBulkCreateService({
	epicIssueType,
	fixVersions,
	assigneeId,
	parentId,
	labels,
	issueTypeId,
	cards,
	storeFieldsFromFetchIssuesData,
	projectId,
	cloudId,
	sprintFieldId,
	sprintIds,
}: BulkCreateServiceProps): ObservableType<Card[]> {
	return bulkCreateIssuesWithDescription({
		projectId: String(projectId),
		cloudId,
		issueTypeId,
		parentId: parentId ? String(parentId) : null,
		fixVersions,
		assigneeId,
		labels,
		sprintIds,
		sprintFieldId,
		cards,
	})
		.map((response) =>
			transformBulkCreationResponseToCMPIssues(response, {
				projectId,
				flagged: false, // Assume false
				done: false, // Assume false
				hidden: false, // Assume false
				dueDate: undefined, // Assume undefined
				fixVersions, // Assume successful
				sprintIds: fg('ai_powered_backlog_inline_create-create_in_sprint') ? sprintIds ?? [] : [],
			}),
		)
		.map((issues) => {
			storeFieldsFromFetchIssuesData(issues, epicIssueType);

			return issues;
		})
		.map((issues) => issues.map(transformCard));
}

export function transformBulkCreationResponseToCMPIssues(
	response: BulkIssueCreationGraphqlResponse,
	overrides: {
		projectId: number;
		flagged: boolean;
		done: boolean;
		hidden: boolean;
		dueDate: string | undefined;
		fixVersions: string[];
		sprintIds: string[];
	},
): Array<Issue> {
	return Object.values(response)
		.map((creationRequest) => {
			if (!creationRequest.createIssue?.issue) {
				return null;
			}

			const { issue } = creationRequest.createIssue;

			// Construct the Issue object
			const formattedIssue = {
				id: Number(issue.issueId),
				key: issue.key,
				summary: issue.summary,
				labels: issue.fieldSetsById.edges
					.flatMap((edge) => edge.node.fields.edges)
					.flatMap((fieldEdge) => fieldEdge.node.labels.edges)
					.map((labelEdge) => labelEdge.node.name),
				...(issue.parentIssueField.parentIssue
					? {
							parentId: Number(issue.parentIssueField.parentIssue.issueId),
							parentKey: issue.parentIssueField.parentIssue.key,
							epicField: {
								issueId: issue.parentIssueField.parentIssue.issueId,
								epicKey: issue.parentIssueField.parentIssue.key,
								summary: issue.parentIssueField.parentIssue.summary,
								text: issue.parentIssueField.parentIssue.summary,
								issueTypeId:
									issue.parentIssueField.parentIssue.issueTypeField.issueType.issueTypeId,
								label: issue.parentIssueField.parentIssue.issueTypeField.issueType.name,
								issueTypeIconUrl:
									issue.parentIssueField.parentIssue.issueTypeField.issueType.avatar.medium,
								color: '',
								epicColor: '',
							},
						}
					: {
							parentId: undefined,
							parentKey: undefined,
							epicField: undefined,
						}),
				...(issue.priorityField?.priority
					? {
							priorityUrl: issue.priorityField.priority.iconUrl,
							priorityName: issue.priorityField.priority.name,
						}
					: {
							priorityUrl: '',
							priorityName: '',
						}),
				statusId: issue.statusField.status.statusId,
				statusName: issue.statusField.status.name,
				statusUrl: '',
				status: {
					id: issue.statusField.status.statusId,
					name: issue.statusField.status.name,
					description: issue.statusField.status.description,
					iconUrl: '',
					statusCategory: {
						id: issue.statusCategory.statusCategoryId,
						key: issue.statusCategory.key,
						colorName: issue.statusCategory.colorName,
					},
				},
				...(issue.assigneeField.user
					? {
							assignee: issue.assigneeField.user.name,
							assigneeAccountId: issue.assigneeField.user.accountId,
							assigneeKey: issue.assigneeField.user.id,
							assigneeName: issue.assigneeField.user.name,
						}
					: {
							assignee: undefined,
							assigneeAccountId: undefined,
							assigneeKey: undefined,
							assigneeName: undefined,
						}),
				avatarUrl: issue.assigneeField.user?.picture,
				typeName: issue.issueTypeField.issueType.name,
				typeId: issue.issueTypeField.issueType.issueTypeId,
				typeHierarchyLevel: issue.issueTypeField.issueType.hierarchy.level,
				typeUrl: issue.issueTypeField.issueType.avatar.medium,

				// Specified in service call
				dueDate: overrides.dueDate,
				hidden: overrides.hidden,
				done: overrides.done,
				flagged: overrides.flagged,
				fixVersions: overrides.fixVersions.map((v) => Number(v)),
				sprintIds: overrides.sprintIds.map((v) => Number(v)),
				projectId: overrides.projectId,

				// Use constant for the time being
				extraFields: undefined,
				linkedPagesCount: 0,
				estimateStatisticRequired: false,
				columnStatistic: {
					statFieldId: '',
					statFieldValue: {
						text: '',
						value: 0,
					},
				},
			};

			return formattedIssue;
		})
		.filter(Boolean);
}
